import { useEffect, useState } from "react";

/**
 * Largura real do elemento, observada em tempo real.
 *
 * Serve para telas que aparecem em contextos de largura diferente — a mesma tela dentro de um
 * drawer estreito e ocupando a página inteira. Media query não resolve esse caso, porque ela
 * mede a janela, não o espaço que o componente realmente recebeu.
 *
 * @example
 * const [ref, largura] = useLarguraElemento<HTMLDivElement>();
 * const compacto = largura > 0 && largura < 720;
 * return <div ref={ref}>...</div>;
 */
export function useLarguraElemento<T extends HTMLElement>(): [(elemento: T | null) => void, number] {
    const [elemento, setElemento] = useState<T | null>(null);
    const [largura, setLargura] = useState(0);

    useEffect(() => {
        if (!elemento) {
            return;
        }

        // Antes da primeira medição a largura é 0; quem consome deve tratar esse estado para
        // não piscar no layout errado durante o primeiro render.
        setLargura(elemento.getBoundingClientRect().width);

        const observador = new ResizeObserver(([entrada]) => {
            setLargura(entrada.contentRect.width);
        });

        observador.observe(elemento);

        return () => observador.disconnect();
    }, [elemento]);

    return [setElemento, largura];
}
